import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@unboared/react';

import { LoadingPage } from '~/pages/LoadingPage';
import { Button } from '~/components/ui/button';
import { SUPPORT_EMAIL } from '~/config/global.config';
import { reloadScreen } from '~/unboared/config/screenSocketAudit';

import styles from './ScreenConnectLoader.module.css';

/**
 * Sans session prête au bout de ce délai, on passe du spinner au message
 * d'aide. Généreux : une création de session lente (réseau chargé, réveil du
 * serveur) ne doit pas être prise pour une panne.
 */
const CONNECT_TIMEOUT_MS = 20_000;

type Stall = 'unreachable' | 'stuck';

/** Le socket brut de la lib (même accès que GamepadLoader). */
function socketConnected(): boolean {
    try {
        const a: any = api();
        const wss = a?.getWSS?.() ?? a?.wss;
        return Boolean(wss?.connected);
    } catch {
        return false;
    }
}

export type ScreenConnectLoaderProps = {
    /** Hôte réellement composé par le socket (ex. `ws.unboared.com`). */
    serverHost: string;
    /** Écran sans opérateur (mode auto / aéroports) : pas de bouton ni de support. */
    unattended?: boolean;
};

/**
 * Loader écran passé à `UnboaredGame` : affiché tant que la session n'est pas
 * prête (connexion socket + création/reprise de session).
 *
 * Le spinner seul tournait sans fin quand le navigateur n'atteignait jamais le
 * serveur (réseau d'entreprise qui filtre `ws.unboared.com` — cas Thales du
 * 2026-09-15) : aucun indice sur quoi faire. Passé `CONNECT_TIMEOUT_MS`, on lit
 * l'état du socket pour afficher le bon diagnostic :
 * - socket jamais connecté → réseau : domaine à autoriser, autre réseau ;
 * - socket connecté mais session jamais prête → serveur : réessayer / support.
 *
 * La reprise n'est pas touchée : socket.io continue de retenter en arrière-plan
 * et, dès que la session est prête, `UnboaredGame` démonte ce loader de lui-même.
 */
export function ScreenConnectLoader({ serverHost, unattended = false }: ScreenConnectLoaderProps) {
    const { t } = useTranslation();
    const [stall, setStall] = useState<Stall | null>(null);

    useEffect(() => {
        const timer = setTimeout(() => setStall(socketConnected() ? 'stuck' : 'unreachable'), CONNECT_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, []);

    if (!stall) {
        return <LoadingPage>{t('home.connectServer')}</LoadingPage>;
    }

    return (
        <div className={styles.root}>
            {stall === 'unreachable' ? (
                <>
                    <p className={styles.title}>{t('home.connectTimeout.title')}</p>
                    <p className={styles.hint}>{t('home.connectTimeout.hint', { host: serverHost })}</p>
                    <ul className={styles.tips}>
                        <li>{t('home.connectTimeout.tipNetwork', { host: serverHost })}</li>
                        <li>{t('home.connectTimeout.tipOther')}</li>
                    </ul>
                </>
            ) : (
                <>
                    <p className={styles.title}>{t('home.connectTimeout.stuckTitle')}</p>
                    <p className={styles.hint}>{t('home.connectTimeout.stuckHint', { host: serverHost })}</p>
                </>
            )}
            {!unattended && (
                <>
                    <Button className={styles.retry} onClick={() => reloadScreen('bouton-reessayer')}>
                        {t('home.connectTimeout.retry')}
                    </Button>
                    <p className={styles.support}>
                        {t('home.connectTimeout.support')}{' '}
                        <a className={styles.supportLink} href={`mailto:${SUPPORT_EMAIL}`}>
                            {SUPPORT_EMAIL}
                        </a>
                    </p>
                </>
            )}
        </div>
    );
}
